// The order vocabulary the console shares with the apps (ICD C18/C20): the
// status pill, the journey's steps and the filter chips. Pure, so it is tested.
import { S } from './strings';
import type { OpsOrder, OrderStatus } from './types';

export type Tone = 'violet' | 'blue' | 'green' | 'red' | 'grey' | 'amber';

const TONE: Record<OrderStatus, Tone> = {
  placed: 'violet', assigned: 'blue', agent_en_route: 'blue', tech_arrived: 'blue', install_in_progress: 'blue',
  install_completed: 'green', delivered: 'green', completed: 'green', failed_first_install: 'red',
  cancelled_by_customer: 'grey', cancelled_by_agency: 'grey',
};

export const statusPill = (s: OrderStatus): [string, Tone] => [S.st[s], TONE[s]];

export const isOpen = (s: OrderStatus) =>
  !['completed', 'cancelled_by_customer', 'cancelled_by_agency', 'failed_first_install'].includes(s);
export const isDone = (s: OrderStatus) => s === 'completed' || s === 'install_completed' || s === 'delivered';
export const isCancelled = (s: OrderStatus) => s.startsWith('cancelled');

export type StepKey = 'placed' | 'assigned' | 'enroute' | 'arrived' | 'install' | 'install_done' | 'completed';
export interface Step { key: StepKey; label: string; icon: string; status: OrderStatus }

export function steps(o: Pick<OpsOrder, 'order_type'>): Step[] {
  const base: Step[] = [
    { key: 'placed', label: S.steps.placed, icon: 'doc', status: 'placed' },
    { key: 'assigned', label: S.steps.assigned, icon: 'hand', status: 'assigned' },
    { key: 'enroute', label: S.steps.enroute, icon: 'moto', status: 'agent_en_route' },
    { key: 'arrived', label: S.steps.arrived, icon: 'house', status: 'tech_arrived' },
  ];
  return o.order_type === 'first_install'
    ? [...base, { key: 'install', label: S.steps.install, icon: 'tool', status: 'install_in_progress' },
        { key: 'install_done', label: S.steps.install_done, icon: 'check', status: 'install_completed' }]
    : [...base, { key: 'completed', label: S.steps.completed, icon: 'check', status: 'completed' }];
}

/** Index of the step the order stands on. A failed install stops at the door; a cancelled order at the start. */
export function stepIdx(o: Pick<OpsOrder, 'order_type' | 'status'>): number {
  const inst = o.order_type === 'first_install';
  switch (o.status) {
    case 'placed': return 0;
    case 'assigned': return 1;
    case 'agent_en_route': return 2;
    case 'tech_arrived': return 3;
    case 'install_in_progress': return 4;
    case 'install_completed': return 5;
    case 'completed': case 'delivered': return inst ? 5 : 4;
    case 'failed_first_install': return 3;
    default: return 0;
  }
}

/** When the order reached each step, from its events (the latest time per status). */
export function stepTimes(o: Pick<OpsOrder, 'order_type' | 'events' | 'created_at'>): (string | null)[] {
  return steps(o).map((s) => (s.key === 'placed' ? o.events.placed ?? o.created_at : o.events[s.status] ?? (s.key === 'completed' ? o.events.delivered ?? null : null)));
}

export type OrdFilter = 'all' | 'placed' | 'run' | 'done' | 'prob';
export const ORDF: [OrdFilter, string | null][] = [
  ['all', null], ['placed', 'var(--violet)'], ['run', 'var(--blue)'], ['done', 'var(--green)'], ['prob', 'var(--red)'],
];

export function ordMatch(o: Pick<OpsOrder, 'status'>, f: OrdFilter) {
  switch (f) {
    case 'all': return true;
    case 'placed': return o.status === 'placed';
    case 'run': return ['assigned', 'agent_en_route', 'tech_arrived', 'install_in_progress'].includes(o.status);
    case 'done': return isDone(o.status);
    case 'prob': return o.status === 'failed_first_install' || isCancelled(o.status);
  }
}

/** What the customer pays: gas and delivery in cash, plus the kit for a first install. */
export function amount(o: Pick<OpsOrder, 'gas_price' | 'delivery_fee' | 'fare' | 'kit'>) {
  const cash = (o.gas_price ?? 0) + (o.delivery_fee ?? o.fare ?? 0);
  return { cash, kit: o.kit?.price ?? 0, total: cash + (o.kit?.price ?? 0) };
}

export function matchesQuery(o: OpsOrder, q: string) {
  if (!q) return true;
  const hay = [o.ref, o.customer.name, o.customer.quartier, o.marketer?.name, o.rider?.name, o.agency.name].join(' ').toLowerCase();
  return hay.includes(q.toLowerCase());
}
